/**
 * Satellite Tracing View page.
 * See wireframe: section 4 Satellite / Tracing View
 *
 * Uses MapView for the Mapbox GL JS satellite map and AddressSearch
 * for geocoding. Tool buttons, zoom controls, continue-to-design,
 * and status bar are wired here.
 */

import type { RouteParams } from '@/router/router';

export function SatellitePage(params: RouteParams): HTMLElement {
  const projectId = params['projectId'] ?? '';

  const page = document.createElement('div');
  page.className = 'satellite-page';
  page.style.cssText = 'height:100%;display:flex;flex-direction:column;overflow:hidden;';

  page.innerHTML = `
    <!-- Top Bar -->
    <header class="topbar">
      <a class="btn btn-ghost btn-sm" href="#/dashboard">
        <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd" />
        </svg>
        Back
      </a>
      <span class="topbar-title">Trace Roof Outline</span>
      <button class="btn btn-primary btn-sm" id="continue-btn" disabled>
        Continue to Design
        <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fill-rule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clip-rule="evenodd" />
        </svg>
      </button>
    </header>

    <!-- Address Bar -->
    <div class="satellite-address-bar">
      <div class="satellite-address-input-group">
        <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" class="address-icon" aria-hidden="true">
          <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd" />
        </svg>
        <input
          type="text"
          id="address-input"
          class="address-input"
          placeholder="Enter property address…"
          aria-label="Property address"
        />
        <button class="btn btn-primary btn-sm" id="search-btn">Search</button>
      </div>
    </div>

    <!-- Map Container -->
    <div class="satellite-map-container" id="map-container" role="application" aria-label="Satellite map"></div>

    <!-- Bottom Toolbar -->
    <div class="satellite-toolbar">
      <div class="satellite-tools" role="toolbar" aria-label="Tracing tools">
        <button class="btn btn-ghost btn-sm active" id="tool-trace" data-tool="trace" aria-pressed="true">
          Trace
        </button>
        <button class="btn btn-ghost btn-sm" id="tool-edit" data-tool="edit" aria-pressed="false">
          Edit
        </button>
        <button class="btn btn-ghost btn-sm" id="tool-delete" data-tool="delete" aria-pressed="false">
          Delete
        </button>
        <div class="toolbar-separator-v"></div>
        <button class="btn btn-ghost btn-sm" id="undo-btn" title="Undo (Ctrl+Z)" disabled>Undo</button>
        <button class="btn btn-ghost btn-sm" id="redo-btn" title="Redo (Ctrl+Shift+Z)" disabled>Redo</button>
        <div class="toolbar-separator-v"></div>
        <button class="btn btn-ghost btn-sm" id="zoom-in-btn" aria-label="Zoom in">+</button>
        <button class="btn btn-ghost btn-sm" id="zoom-out-btn" aria-label="Zoom out">&minus;</button>
      </div>
      <div class="satellite-status">
        <span id="buildings-count">Buildings traced: 0</span>
        <span class="status-separator">|</span>
        <span id="footprint-area">Footprint area: &mdash;</span>
      </div>
    </div>
  `;

  // Initialise Mapbox map lazily (after DOM mount)
  requestAnimationFrame(() => {
    initSatelliteMap(page, projectId);
  });

  return page;
}

// ============================================================
// Map Initialisation
// ============================================================

async function initSatelliteMap(page: HTMLElement, projectId: string): Promise<void> {
  // Dynamic imports for code-splitting (mapbox-gl is large)
  const { MapView } = await import('@/satellite/MapView');
  const { geocodeAddress } = await import('@/satellite/AddressSearch');

  const container = page.querySelector<HTMLElement>('#map-container')!;

  // Mapbox token from Vite env or global variable fallback
  const accessToken =
    import.meta.env.VITE_MAPBOX_TOKEN
    ?? (window as unknown as Record<string, string>)['MAPBOX_TOKEN']
    ?? '';

  const mapView = new MapView({ container, accessToken });

  // ----------------------------------------------------------
  // Address search
  // ----------------------------------------------------------
  const searchBtn = page.querySelector<HTMLButtonElement>('#search-btn')!;
  const addressInput = page.querySelector<HTMLInputElement>('#address-input')!;

  const handleSearch = async () => {
    const query = addressInput.value.trim();
    if (!query) return;

    searchBtn.disabled = true;
    searchBtn.textContent = 'Searching…';

    try {
      const result = await geocodeAddress(query, accessToken);

      if (result) {
        mapView.flyTo(result.lng, result.lat);
        // Update input with the canonical place name
        addressInput.value = result.placeName;
      } else {
        addressInput.setCustomValidity('No results found for this address.');
        addressInput.reportValidity();
        // Clear validity after a moment so user can retry
        setTimeout(() => addressInput.setCustomValidity(''), 3000);
      }
    } catch {
      addressInput.setCustomValidity('Search failed. Check your connection and try again.');
      addressInput.reportValidity();
      setTimeout(() => addressInput.setCustomValidity(''), 3000);
    } finally {
      searchBtn.disabled = false;
      searchBtn.textContent = 'Search';
    }
  };

  searchBtn.addEventListener('click', handleSearch);
  addressInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') handleSearch();
  });

  // ----------------------------------------------------------
  // Tracing tool (must wait for map style to load)
  // ----------------------------------------------------------
  await mapView.whenReady();
  const { TracingTool } = await import('@/satellite/TracingTool');
  const { formatFootprintArea } = await import('@/satellite/FootprintConverter');

  const tracingTool = new TracingTool(mapView.getMap());

  const buildingsCountEl = page.querySelector<HTMLElement>('#buildings-count')!;
  const footprintAreaEl = page.querySelector<HTMLElement>('#footprint-area')!;
  const undoBtn = page.querySelector<HTMLButtonElement>('#undo-btn')!;
  const redoBtn = page.querySelector<HTMLButtonElement>('#redo-btn')!;
  const continueBtn = page.querySelector<HTMLButtonElement>('#continue-btn')!;

  const updateStatus = ({ count, totalAreaMm2 }: { count: number; totalAreaMm2: number }) => {
    buildingsCountEl.textContent = `Buildings traced: ${count}`;
    footprintAreaEl.innerHTML =
      count > 0
        ? `Footprint area: ${formatFootprintArea(totalAreaMm2)}`
        : `Footprint area: \u2014`;
    continueBtn.disabled = count === 0;
    undoBtn.disabled = !tracingTool.canUndo();
    redoBtn.disabled = !tracingTool.canRedo();
  };

  tracingTool.on(updateStatus);

  // Initialise status
  updateStatus({ count: 0, totalAreaMm2: 0 });

  // ----------------------------------------------------------
  // Continue to Design
  // ----------------------------------------------------------
  continueBtn.addEventListener('click', () => {
    const footprintSet = tracingTool.exportFootprints();

    // Store footprints in sessionStorage for the editor to pick up on load
    try {
      sessionStorage.setItem(
        `roofer_satellite_footprints_${projectId}`,
        JSON.stringify(footprintSet),
      );
    } catch {
      // sessionStorage full or unavailable — proceed anyway
    }

    import('@/router/router').then(({ router }) => {
      router.navigate(`/editor/${projectId}`);
    });
  });

  // ----------------------------------------------------------
  // Tool switching
  // ----------------------------------------------------------
  page.querySelectorAll<HTMLButtonElement>('[data-tool]').forEach(btn => {
    btn.addEventListener('click', () => {
      page.querySelectorAll<HTMLButtonElement>('[data-tool]').forEach(b => {
        b.classList.remove('active');
        b.setAttribute('aria-pressed', 'false');
      });
      btn.classList.add('active');
      btn.setAttribute('aria-pressed', 'true');

      const toolName = btn.dataset['tool'] as 'trace' | 'edit' | 'delete';
      tracingTool.setMode(toolName);
    });
  });

  // Undo / Redo buttons
  undoBtn.addEventListener('click', () => {
    tracingTool.undo();
    undoBtn.disabled = !tracingTool.canUndo();
    redoBtn.disabled = !tracingTool.canRedo();
  });
  redoBtn.addEventListener('click', () => {
    tracingTool.redo();
    undoBtn.disabled = !tracingTool.canUndo();
    redoBtn.disabled = !tracingTool.canRedo();
  });

  // ----------------------------------------------------------
  // Zoom controls
  // ----------------------------------------------------------
  page.querySelector('#zoom-in-btn')?.addEventListener('click', () => mapView.zoomIn());
  page.querySelector('#zoom-out-btn')?.addEventListener('click', () => mapView.zoomOut());

  // ----------------------------------------------------------
  // Cleanup on unmount
  // ----------------------------------------------------------
  const observer = new MutationObserver(() => {
    if (!document.body.contains(page)) {
      tracingTool.dispose();
      mapView.dispose();
      observer.disconnect();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}
